/**
 * MECANISMO OFICIAL DE SENHA — PONTO ÚNICO.
 *
 * Nenhum outro arquivo gera, grava ou compara senha. Antes deste módulo
 * existiam três comparadores divergentes (o domínio, o servidor Express e o
 * AdminService) e a "senha guardada" era o texto puro, às vezes prefixado de
 * `hash_`. Três consequências reais:
 *
 *   - quem lesse o cadastro lia a senha de todo mundo;
 *   - `passwordMatches` aceitava `password === stored`, então digitar o
 *     literal `hash_123456` entrava na conta cuja senha era `123456`;
 *   - o servidor aceitava o próprio valor gravado como se fosse a senha.
 *
 * Aqui a senha vira PBKDF2-SHA-256 com sal aleatório por usuário, pela
 * WebCrypto — a mesma API no navegador (teste local) e no Node (servidor e
 * testes), sem dependência nova. O formato é autoexplicativo e versionado no
 * próprio valor, para que aumentar o custo no futuro não invalide o que já
 * está gravado:
 *
 *     pbkdf2$sha256$<iterações>$<sal base64>$<derivado base64>
 *
 * As funções são assíncronas porque `crypto.subtle` é assíncrona. Isso é
 * proposital: obriga quem autentica a passar pelo caminho oficial.
 */

const ALGORITHM = "pbkdf2";
const DIGEST = "sha256";
/** Custo atual. Gravado em cada valor, então pode subir sem migração. */
const ITERATIONS = 210000;
const SALT_BYTES = 16;
const KEY_BITS = 256;

const LEGACY_PREFIX = "hash_";

function subtle(): SubtleCrypto {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c || !c.subtle) {
    throw new Error("WebCrypto indisponível: não é possível tratar senha com segurança neste ambiente.");
  }
  return c.subtle;
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  (globalThis as { crypto: Crypto }).crypto.getRandomValues(bytes);
  return bytes;
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  if (typeof btoa === "function") return btoa(binary);
  return Buffer.from(bytes).toString("base64");
}

function fromBase64(value: string): Uint8Array {
  if (typeof atob === "function") {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }
  return new Uint8Array(Buffer.from(value, "base64"));
}

async function derive(password: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const key = await subtle().importKey("raw", new TextEncoder().encode(password), "PBKDF2", false, ["deriveBits"]);
  const bits = await subtle().deriveBits(
    { name: "PBKDF2", salt: salt as unknown as BufferSource, iterations, hash: "SHA-256" },
    key,
    KEY_BITS
  );
  return new Uint8Array(bits);
}

/** Comparação de tempo constante — não vaza quantos bytes iniciais bateram. */
function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= (a[i] as number) ^ (b[i] as number);
  return diff === 0;
}

/** `true` para um valor já no formato oficial (e não para texto puro / `hash_...`). */
export function isHashedPassword(stored: string | null | undefined): boolean {
  if (typeof stored !== "string") return false;
  const parts = stored.split("$");
  return parts.length === 5 && parts[0] === ALGORITHM && parts[1] === DIGEST;
}

/** Deriva o valor a gravar. Duas chamadas com a mesma senha dão valores diferentes (sal). */
export async function hashPassword(password: string): Promise<string> {
  const plain = String(password ?? "");
  if (!plain) throw new Error("Senha vazia não pode ser registrada.");
  const salt = randomBytes(SALT_BYTES);
  const derived = await derive(plain, salt, ITERATIONS);
  return [ALGORITHM, DIGEST, String(ITERATIONS), toBase64(salt), toBase64(derived)].join("$");
}

/** Confere a senha contra um valor no formato oficial. Qualquer outro formato é `false`. */
export async function verifyPassword(password: string, stored: string | null | undefined): Promise<boolean> {
  if (!password || !isHashedPassword(stored)) return false;
  const [, , iterationsRaw, saltRaw, expectedRaw] = (stored as string).split("$");
  const iterations = Number(iterationsRaw);
  if (!Number.isInteger(iterations) || iterations <= 0 || !saltRaw || !expectedRaw) return false;
  try {
    const derived = await derive(password, fromBase64(saltRaw), iterations);
    return equalBytes(derived, fromBase64(expectedRaw));
  } catch {
    return false;
  }
}

/**
 * Credencial antiga, de antes deste módulo: senha em texto puro no campo
 * `password`, ou `hash_<senha>` em `passwordHash`. Aceita só para que as
 * contas já existentes continuem entrando — e quem chama é obrigado a
 * regravar a senha no formato oficial logo em seguida (ver
 * `AdminService.authenticateByCredentials`). Repare no que NÃO está aqui:
 * comparar a senha digitada com o valor GRAVADO. Era isso que deixava
 * `hash_123456` funcionar como senha.
 */
export function matchesLegacyPassword(password: string, record: { password?: string | undefined; passwordHash?: string | undefined }): boolean {
  if (!password) return false;
  const plain = record.password;
  if (typeof plain === "string" && plain.length > 0 && !isHashedPassword(plain) && plain === password) return true;
  const legacy = record.passwordHash;
  if (typeof legacy === "string" && legacy.startsWith(LEGACY_PREFIX) && legacy === `${LEGACY_PREFIX}${password}`) return true;
  return false;
}

/** `true` quando o registro ainda guarda a senha de forma insegura. */
export function needsPasswordUpgrade(record: { password?: string | undefined; passwordHash?: string | undefined }): boolean {
  if (typeof record.password === "string" && record.password.length > 0) return true;
  return !isHashedPassword(record.passwordHash);
}
